<template>
    <div class="data-page">
        <PageHeader title="Fornecedores" :subtitle="summaryText">
            <template #actions>
                <Button label="Cadastrar Fornecedor" icon="pi pi-plus" @click="openCreateModal" />
            </template>
        </PageHeader>

        <div class="data-card">
            <div class="data-card-toolbar">
                <span class="p-input-icon-left data-search">
                    <i class="pi pi-search" />
                    <InputText type="text" v-model="state.search" placeholder="Buscar por fornecedor, tipo ou usuário..." />
                </span>
            </div>

            <DataTable class="data-table" paginator :value="filteredVendors" :rowsPerPageOptions="[20, 50, 100]" lazy
                :loading="stateVendors.loading.vendors" :rows="stateVendors.perPage" ref="dt" dataKey="id"
                :totalRecords="stateVendors.totalRecords" :autoLayout="true" responsiveLayout="scroll"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                @page="onPage($event)">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ state.search ? 'Nenhum fornecedor encontrado para essa busca.' : 'Nenhum fornecedor cadastrado.' }}</span>
                    </div>
                </template>

                <Column field="id" header="ID" class="col-id">
                    <template #body="slotProps">
                        <span class="record-id">{{ formatId(slotProps.data.id) }}</span>
                    </template>
                </Column>
                <Column field="name" header="Fornecedor">
                    <template #body="slotProps">
                        <span class="record-name">{{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column field="type" header="Tipo">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.type" class="pill pill-info">{{ slotProps.data.type }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="deposit_tax_value" header="Taxa de Depósito">
                    <template #body="slotProps">
                        <span v-if="formatTransactionTaxValue(slotProps.data.deposit_tax_type, slotProps.data.deposit_tax_value)">
                            {{ formatTransactionTaxValue(slotProps.data.deposit_tax_type, slotProps.data.deposit_tax_value) }}
                        </span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="withdraw_tax_value" header="Taxa de Saque">
                    <template #body="slotProps">
                        <span v-if="formatTransactionTaxValue(slotProps.data.withdraw_tax_type, slotProps.data.withdraw_tax_value)">
                            {{ formatTransactionTaxValue(slotProps.data.withdraw_tax_type, slotProps.data.withdraw_tax_value) }}
                        </span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="active" header="Ativo">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="slotProps.data.active ? 'is-on' : 'is-off'">
                            <span class="pill-dot"></span>
                            {{ changeStatusBoleanToText(slotProps.data.active) }}
                        </span>
                    </template>
                </Column>
                <Column field="username" header="Usuário">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.username">{{ slotProps.data.username }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="merchant_id" header="Merchant ID">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.merchant_id" class="mono-chip mono-chip-sm"
                            :title="slotProps.data.merchant_id">{{ slotProps.data.merchant_id }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="token" header="Token">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.token" class="mono-chip mono-chip-sm" :title="slotProps.data.token">{{ slotProps.data.token }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column class="col-actions">
                    <template #header>
                        Ações
                    </template>
                    <template #body="slotProps">
                        <div class="flex justify-content-end">
                            <SplitButton class="table-action-button" label="Ver mais" icon="pi pi-eye"
                                :model="actionMenuItems(slotProps.data)" @click="openDetailsModal(slotProps.data)" />
                        </div>
                    </template>
                </Column>

                <template #paginatorstart>
                    <TableResults :shown="filteredVendors.length" :total="stateVendors.totalRecords"
                        :pageTotal="stateVendors.vendors.length" :filtering="!!state.search" />
                </template>
            </DataTable>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.createModal" :style="{ width: '38rem' }" modal
            header="Cadastrar fornecedor" @update:visible="handleCreateModalClose">

            <form class="data-form-grid" @submit.prevent="tryToRegisterVendor">

                <div class="form-field">
                    <label for="new-vendor-name">Nome</label>
                    <InputText id="new-vendor-name" type="text" v-model="state.newVendor.name" autofocus />
                </div>

                <div class="form-field">
                    <label for="new-vendor-type">Tipo</label>
                    <Dropdown inputId="new-vendor-type" v-model="state.newVendor.type" :options="stateVendors.types"
                        :loading="stateVendors.loading.types" placeholder="Selecione o tipo" />
                </div>

                <div class="form-field">
                    <label for="new-vendor-deposit-tax">Tipo de taxa de depósito</label>
                    <Dropdown inputId="new-vendor-deposit-tax" v-model="state.newVendor.depositTax" optionLabel="value"
                        :options="stateVendors.taxTypes" placeholder="Real ou porcentagem" />
                </div>

                <div class="form-field">
                    <label for="new-vendor-deposit-value">Valor da taxa de depósito</label>
                    <InputNumber inputId="new-vendor-deposit-value" v-model="state.newVendor.depositTaxValue"
                        :prefix="newDepositTaxValueSymbol" :maxFractionDigits="2" />
                </div>

                <div class="form-field">
                    <label for="new-vendor-withdraw-tax">Tipo de taxa de saque</label>
                    <Dropdown inputId="new-vendor-withdraw-tax" v-model="state.newVendor.withdrawTax" optionLabel="value"
                        :options="stateVendors.taxTypes" placeholder="Real ou porcentagem" />
                </div>

                <div class="form-field">
                    <label for="new-vendor-withdraw-value">Valor da taxa de saque</label>
                    <InputNumber inputId="new-vendor-withdraw-value" v-model="state.newVendor.withdrawTaxValue"
                        :prefix="newWithdrawTaxValueSymbol" :maxFractionDigits="2" />
                </div>

                <div class="form-field">
                    <label for="new-vendor-status">Status</label>
                    <Dropdown inputId="new-vendor-status" v-model="state.newVendor.active" optionLabel="name"
                        optionValue="value" :options="activeOptions" />
                </div>

                <template v-if="state.newVendor.type">

                    <div v-if="state.newVendor.type == 'SHIPAY' || state.newVendor.type == 'SICOOB'" class="form-field">
                        <label for="new-vendor-username">Usuário</label>
                        <InputText id="new-vendor-username" type="text" v-model="state.newVendor.username"
                            @update:modelValue="sicoobUsernameToUppercase($event)" />
                    </div>

                    <div v-if="state.newVendor.type == 'METAGRATOR' || state.newVendor.type == 'LIONPAY'"
                        class="form-field">
                        <label for="new-vendor-merchant">{{ fieldLabel(state.newVendor.type, 'merchant_id') }}</label>
                        <InputText id="new-vendor-merchant" type="text" v-model="state.newVendor.merchantId" />
                    </div>

                    <div v-if="state.newVendor.type == 'SICOOB' || state.newVendor.type == 'METAGRATOR'"
                        class="form-field">
                        <label for="new-vendor-password">{{ fieldLabel(state.newVendor.type, 'password') }}</label>
                        <Password inputId="new-vendor-password" v-model="state.newVendor.password" :feedback="false"
                            toggleMask />
                    </div>

                    <div class="form-field">
                        <label for="new-vendor-token">{{ fieldLabel(state.newVendor.type, 'token') }}</label>
                        <InputText id="new-vendor-token" type="text" v-model="state.newVendor.vendorToken" />
                    </div>

                    <div class="form-field">
                        <label for="new-vendor-key">{{ fieldLabel(state.newVendor.type, 'key') }}</label>
                        <Password inputId="new-vendor-key" v-model="state.newVendor.key" :feedback="false" toggleMask />
                    </div>

                </template>

                <button type="submit" class="hidden"></button>
            </form>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="state.createModal = false" />
                <Button :loading="state.loading.create" type="button" label="Registrar fornecedor" icon="pi pi-check"
                    @click="tryToRegisterVendor" />
            </template>

        </Dialog>

        <Dialog class="data-dialog" v-model:visible="state.detailsModal" :style="{ width: '38rem' }" modal
            header="Editar fornecedor">

            <form class="data-form-grid" @submit.prevent="saveChanges">

                <div class="data-dialog-subject">
                    <span class="pill pill-muted">ID {{ formatId(state.vendor.id) }}</span>
                    <span>{{ state.vendor.name }}</span>
                    <span v-if="state.vendor.type" class="pill pill-info">{{ state.vendor.type }}</span>
                </div>

                <div class="form-field">
                    <label for="vendor-name">Nome</label>
                    <InputText id="vendor-name" type="text" v-model="state.modifiedVendor.name" />
                </div>

                <div class="form-field">
                    <label for="vendor-status">Status</label>
                    <Dropdown inputId="vendor-status" v-model="state.modifiedVendor.active" optionLabel="name"
                        optionValue="value" :options="activeOptions" />
                </div>

                <div class="form-field">
                    <label for="vendor-deposit-tax">Tipo de taxa de depósito</label>
                    <Dropdown inputId="vendor-deposit-tax" v-model="state.depositTax" optionLabel="value"
                        :options="stateVendors.taxTypes" />
                </div>

                <div class="form-field">
                    <label for="vendor-deposit-value">Valor da taxa de depósito</label>
                    <InputNumber inputId="vendor-deposit-value" v-model="state.modifiedVendor.deposit_tax_value"
                        :prefix="state.depositPrefixSymbol" :maxFractionDigits="2" />
                </div>

                <div class="form-field">
                    <label for="vendor-withdraw-tax">Tipo de taxa de saque</label>
                    <Dropdown inputId="vendor-withdraw-tax" v-model="state.withdrawTax" optionLabel="value"
                        :options="stateVendors.taxTypes" />
                </div>

                <div class="form-field">
                    <label for="vendor-withdraw-value">Valor da taxa de saque</label>
                    <InputNumber inputId="vendor-withdraw-value" v-model="state.modifiedVendor.withdraw_tax_value"
                        :prefix="state.withdrawPrefixSymbol" :maxFractionDigits="2" />
                </div>

                <div v-if="fieldLabel(state.vendor.type, 'username')" class="form-field">
                    <label for="vendor-username">{{ fieldLabel(state.vendor.type, 'username') }}</label>
                    <InputText id="vendor-username" type="text" v-model="state.modifiedVendor.username" />
                </div>

                <div class="form-field">
                    <label for="vendor-merchant">{{ fieldLabel(state.vendor.type, 'merchant_id') }}</label>
                    <InputText id="vendor-merchant" type="text" v-model="state.modifiedVendor.merchant_id" />
                </div>

                <div class="form-field">
                    <label for="vendor-token">{{ fieldLabel(state.vendor.type, 'token') }}</label>
                    <InputText id="vendor-token" type="text" v-model="state.modifiedVendor.token" />
                </div>

                <div class="form-field">
                    <label for="vendor-password">{{ fieldLabel(state.vendor.type, 'password') }}</label>
                    <Password inputId="vendor-password" v-model="state.modifiedVendor.password" :feedback="false"
                        toggleMask placeholder="Em branco mantém a atual" />
                </div>

                <div class="form-field">
                    <label for="vendor-key">{{ fieldLabel(state.vendor.type, 'key') }}</label>
                    <Password inputId="vendor-key" v-model="state.modifiedVendor.key" :feedback="false" toggleMask
                        placeholder="Em branco mantém a atual" />
                </div>

                <button type="submit" class="hidden"></button>
            </form>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="state.detailsModal = false" />
                <Button :loading="state.loading.save" :disabled="!pendingChanges.length" type="button"
                    label="Salvar alterações" icon="pi pi-check" @click="saveChanges" />
            </template>

        </Dialog>

        <ConfirmDeleteDialog v-model:visible="state.deleteModal" header="Excluir fornecedor"
            :subjectId="state.vendorToDelete.id ? formatId(state.vendorToDelete.id) : null"
            :subjectName="state.vendorToDelete.name" :loading="state.loading.delete"
            @confirm="tryToDeleteVendor">
            Você tem certeza que deseja excluir esse fornecedor? Clientes que dependem apenas dele param de processar depósitos
            e saques.
        </ConfirmDeleteDialog>
    </div>
</template>

<script>
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import InputNumber from 'primevue/inputnumber'
import SplitButton from 'primevue/splitbutton'
import PageHeader from '../../components/PageHeader.vue'
import TableResults from '../../components/TableResults.vue'
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog.vue'
import { computed, reactive, watch } from 'vue'

import services from '../../services'
import useNotificationToast from '../../composables/useNotificationToast'
import useVendors from '../../composables/useVendors'
import { useStore } from 'vuex'

export default {
    components: { Button, DataTable, Column, Dialog, Dropdown, InputText, Password, InputNumber, SplitButton, PageHeader, TableResults, ConfirmDeleteDialog },

    setup() {
        const state = reactive({

            vendor: {},
            modifiedVendor: {},
            vendorToDelete: {},

            search: '',
            depositTax: null,
            depositPrefixSymbol: null,

            withdrawTax: null,
            withdrawPrefixSymbol: null,

            newVendor: {
                name: null,
                type: null,
                depositTax: null,
                depositTaxValue: null,
                withdrawTax: null,
                withdrawTaxValue: null,
                active: true,
                username: null,
                password: null,
                merchantId: null,
                vendorToken: null,
                key: null,
            },

            loading: {
                save: false,
                create: false,
                delete: false,
            },

            currentPage: 1,
            perPage: 20,
            totalRecords: 0,

            createModal: false,
            detailsModal: false,
            deleteModal: false,

        })

        // credenciais em branco no formulário significam "manter a atual"
        const SECRET_FIELDS = ['password', 'key']

        // Cada fornecedor chama as próprias credenciais de um jeito. Só o rótulo muda: coluna e
        // payload continuam os mesmos. `null` esconde o campo que o fornecedor não usa.
        const VENDOR_FIELD_LABELS = {
            LIONPAY: {
                merchant_id: 'Cust ID',
                token: 'App ID',
                key: 'Merchant Key',
                username: null,
            },
            METAGRATOR: {
                merchant_id: 'Platform ID',
                token: 'Public API Key',
                password: 'Private API Key',
                key: 'Webhook Secret',
                username: null,
            },
            default: {
                merchant_id: 'Merchant ID',
                token: 'Token',
                password: 'Senha',
                key: 'Chave',
                username: 'Usuário',
            },
        }

        function fieldLabel(type, field) {
            const labels = VENDOR_FIELD_LABELS[type]

            if (labels && field in labels) return labels[field]

            return VENDOR_FIELD_LABELS.default[field]
        }

        const activeOptions = [
            { name: 'Ativado', value: true },
            { name: 'Desativado', value: false },
        ]

        watch(() => state.depositTax, () => {
            if (!state.depositTax) return
            state.depositPrefixSymbol = getInputNumberPrefixSymbol(state.depositTax)
            state.modifiedVendor.deposit_tax_type = state.depositTax.type
        })

        watch(() => state.withdrawTax, () => {
            if (!state.withdrawTax) return
            state.withdrawPrefixSymbol = getInputNumberPrefixSymbol(state.withdrawTax)
            state.modifiedVendor.withdraw_tax_type = state.withdrawTax.type
        })

        const store = useStore()
        const { success, error } = useNotificationToast()

        const { stateVendors, loadVendors, loadVendorsTypes, onPage, getInputNumberPrefixSymbol } = useVendors()
        loadVendors()
        loadVendorsTypes()

        const newDepositTaxValueSymbol = computed(() => {
            return state.newVendor.depositTax ? getInputNumberPrefixSymbol(state.newVendor.depositTax) : null
        })

        const newWithdrawTaxValueSymbol = computed(() => {
            return state.newVendor.withdrawTax ? getInputNumberPrefixSymbol(state.newVendor.withdrawTax) : null
        })

        const filteredVendors = computed(() => {
            const term = state.search.trim().toLowerCase()

            if (!term) return stateVendors.vendors

            return stateVendors.vendors.filter((vendor) => {
                return [vendor.name, vendor.type, vendor.username, vendor.merchant_id, String(vendor.id)]
                    .some((value) => (value || '').toString().toLowerCase().includes(term))
            })
        })

        const summaryText = computed(() => {
            const total = stateVendors.totalRecords
            const parts = [`${total} ${total === 1 ? 'fornecedor cadastrado' : 'fornecedores cadastrados'}`]

            // a contagem de ativos só é confiável quando todos os fornecedores estão na página atual
            if (stateVendors.vendors.length && stateVendors.vendors.length >= total) {
                parts.push(`${stateVendors.vendors.filter((vendor) => vendor.active).length} ativos`)
            }

            return parts.join(' · ')
        })

        function formatId(id) {
            return String(id).padStart(2, '0')
        }

        function openCreateModal() {
            clearNewVendor()
            state.createModal = true
        }

        function handleCreateModalClose() {
            clearNewVendor()
        }

        function clearNewVendor() {
            state.newVendor.name = null
            state.newVendor.type = null
            state.newVendor.depositTax = null
            state.newVendor.depositTaxValue = null
            state.newVendor.withdrawTax = null
            state.newVendor.withdrawTaxValue = null
            state.newVendor.active = true
            state.newVendor.username = null
            state.newVendor.password = null
            state.newVendor.merchantId = null
            state.newVendor.vendorToken = null
            state.newVendor.key = null
        }

        function sicoobUsernameToUppercase(value) {

            if (state.newVendor.type == 'SICOOB') {
                state.newVendor.username = value.toUpperCase()
            }

        }

        function tryToRegisterVendor() {

            if (
                !state.newVendor.name ||
                !state.newVendor.type ||
                !state.newVendor.depositTax ||
                !state.newVendor.depositTaxValue ||
                !state.newVendor.withdrawTax ||
                !state.newVendor.withdrawTaxValue) {

                return error('Verifique todos os campos!')

            }

            state.loading.create = true

            const token = store.getters.getToken

            services.vendors.register({
                name: state.newVendor.name,
                type: state.newVendor.type,
                depositTaxType: state.newVendor.depositTax.type,
                depositTaxValue: state.newVendor.depositTaxValue,
                withdrawTaxType: state.newVendor.withdrawTax.type,
                withdrawTaxValue: state.newVendor.withdrawTaxValue,
                active: !!state.newVendor.active,
                username: state.newVendor.username,
                password: state.newVendor.password,
                merchantId: state.newVendor.merchantId,
                vendorToken: state.newVendor.vendorToken,
                key: state.newVendor.key,
                token
            }).then(() => {
                success('Fornecedor registrado com sucesso!')
                state.createModal = false
                clearNewVendor()
                loadVendors()
            }).catch((e) => {
                const message = e.response?.data?.message
                if (!message) return error('Não foi possível registrar o fornecedor.')
                error(message.charAt(0).toUpperCase() + message.slice(1))
            }).finally(() => {
                state.loading.create = false
            })

        }

        function openDetailsModal(vendor) {
            state.vendor = vendor
            state.modifiedVendor = { ...vendor }

            // a API devolve 1/0 e as credenciais em texto: status vira booleano e as
            // credenciais começam vazias para não serem reenviadas sem necessidade
            state.modifiedVendor.active = !!vendor.active
            SECRET_FIELDS.forEach((field) => { state.modifiedVendor[field] = null })

            state.depositTax = stateVendors.taxTypes.find((tax) => tax.type === vendor.deposit_tax_type) || null
            state.withdrawTax = stateVendors.taxTypes.find((tax) => tax.type === vendor.withdraw_tax_type) || null

            state.detailsModal = true
        }

        function isSecretField(field) {
            return SECRET_FIELDS.includes(field)
        }

        function changedItems() {
            const changed = []

            for (const key in state.vendor) {

                if (isSecretField(key)) continue

                const original = state.vendor[key]
                const modified = state.modifiedVendor[key]

                if (key === 'active') {

                    if (!!original !== !!modified) {
                        changed.push({ original: !!original, modified: !!modified, field: key })
                    }

                    continue
                }

                if (original != modified) {
                    changed.push({
                        original,
                        modified,
                        field: key
                    })
                }
            }

            SECRET_FIELDS.forEach((field) => {
                if (state.modifiedVendor[field]) changed.push({ field })
            })

            return changed
        }

        const pendingChanges = computed(() => changedItems())

        function changeStatusBoleanToText(active) {
            return active ? 'Ativado' : 'Desativado'
        }

        function formatTransactionTaxValue(transactionTaxType, transactionTaxValue) {

            if (transactionTaxType == null || transactionTaxValue == null) {
                return ''
            } else {
                return transactionTaxType === 'REAL' ? `R$ ${transactionTaxValue}` : `${transactionTaxValue} %`
            }

        }

        function saveChanges() {

            if (!pendingChanges.value.length) return

            state.loading.save = true

            const modifiedVendor = { ...state.modifiedVendor }

            modifiedVendor.active = !!modifiedVendor.active

            // credencial em branco mantém a que já está gravada
            SECRET_FIELDS.forEach((field) => {
                if (!modifiedVendor[field]) delete modifiedVendor[field]
            })

            const token = store.getters.getToken

            services.vendors.update({ modifiedVendor, token }).then(() => {
                success('Fornecedor atualizado com sucesso')
                state.detailsModal = false
                loadVendors()
            }).catch((e) => {
                // O servidor recusa credencial faltando, chave repetida e conta com transação
                // em aberto: a mensagem dele diz o que fazer, o genérico não.
                const message = e.response?.data?.message
                if (!message) return error('Não foi possível atualizar o fornecedor.')
                error(message.charAt(0).toUpperCase() + message.slice(1))
            }).finally(() => {
                state.loading.save = false
            })

        }

        function actionMenuItems(vendor) {
            return [
                {
                    label: 'Excluir fornecedor',
                    icon: 'pi pi-trash',
                    class: 'text-red-400',
                    command: () => openDeleteModal(vendor)
                },
            ]
        }

        function openDeleteModal(vendor) {
            state.vendorToDelete = vendor
            state.deleteModal = true
        }

        function tryToDeleteVendor() {

            state.loading.delete = true

            const vendorId = state.vendorToDelete.id
            const token = store.getters.getToken

            services.vendors.delete({ vendorId, token }).then(() => {
                success('Fornecedor excluído com sucesso')
                state.deleteModal = false
                state.vendorToDelete = {}
                loadVendors()
            }).catch((e) => {
                const message = e.response?.data?.message
                if (!message) return error('Não foi possível excluir o fornecedor.')
                error(message.charAt(0).toUpperCase() + message.slice(1))
            }).finally(() => {
                state.loading.delete = false
            })

        }

        function getTrasactionTypeLabel(item) {
            return item === 'REAL' ? 'Real R$' : 'Porcentagem %'
        }


        return {
            state,
            stateVendors,
            activeOptions,
            filteredVendors,
            summaryText,
            pendingChanges,
            fieldLabel,
            formatId,
            newDepositTaxValueSymbol,
            newWithdrawTaxValueSymbol,
            onPage,
            openCreateModal,
            handleCreateModalClose,
            sicoobUsernameToUppercase,
            tryToRegisterVendor,
            actionMenuItems,
            openDetailsModal,
            openDeleteModal,
            tryToDeleteVendor,
            changeStatusBoleanToText,
            formatTransactionTaxValue,
            getInputNumberPrefixSymbol,
            saveChanges,
            getTrasactionTypeLabel
        }

    }
}
</script>